// Formatting shared by server and browser. Everything follows English (UK) conventions:
// "Fri 16 Oct", 24-hour times, "€766,365".

import { CURRENCIES } from './constants';

const SYMBOLS = new Map(CURRENCIES.map((c) => [c.code, c.symbol]));
const intFmt = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 0 });
const decFmt = new Intl.NumberFormat('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const exactFmt = new Intl.NumberFormat('en-GB', { maximumFractionDigits: 20 });

export function initials(name: string | null | undefined): string {
  const words = (name ?? '')
    .replace(/^(dr|mr|mrs|ms|prof)\.?\s+/i, '')
    .split(/[\s\-]+/)
    .filter((w) => /[\p{L}\p{N}]/u.test(w));
  if (words.length === 0) return '?';
  const first = [...words[0]!][0] ?? '';
  const last = words.length > 1 ? ([...words[words.length - 1]!][0] ?? '') : ([...words[0]!][1] ?? '');
  return (first + last).toUpperCase();
}

export function formatNumber(n: number): string {
  return intFmt.format(Math.round(n));
}

/** A number with the decimals it was saved with and no padding: "15.5", "1,234.25", "3". For custom number fields. */
export function formatDecimal(n: number): string {
  return exactFmt.format(n);
}

/** "€766,365", "£38,500", "CHF 72,000". Pass `decimals` for exact amounts such as AI costs. */
export function formatMoney(value: number | string | null | undefined, currency: string, opts: { decimals?: boolean } = {}): string {
  const n = typeof value === 'string' ? Number(value) : (value ?? 0);
  const body = opts.decimals ? decFmt.format(n) : intFmt.format(Math.round(n));
  const sign = n < 0 ? '−' : '';
  const abs = body.replace('-', '');
  const symbol = SYMBOLS.get(currency);
  return symbol ? `${sign}${symbol}${abs}` : `${sign}${currency} ${abs}`;
}

/** "€1.2M", "€84k" for tight spaces such as column totals. */
export function formatMoneyShort(value: number, currency: string): string {
  const abs = Math.abs(value);
  let body: string;
  if (abs >= 1_000_000) body = `${(value / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1).replace(/\.0$/, '')}M`;
  else if (abs >= 10_000) body = `${Math.round(value / 1000)}k`;
  else return formatMoney(value, currency);
  const symbol = SYMBOLS.get(currency);
  return symbol ? `${symbol}${body}` : `${currency} ${body}`;
}

/** US dollars with cents, or a tenth of a cent for small AI costs: "$0.006", "$12.40". */
export function formatUsd(value: number | string): string {
  const n = typeof value === 'string' ? Number(value) : value;
  if (n === 0) return '$0.00';
  if (Math.abs(n) < 0.1) return `$${n.toFixed(3)}`;
  return `$${decFmt.format(n)}`;
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let v = bytes / 1024;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v >= 100 || i === 0 ? Math.round(v) : v.toFixed(1).replace(/\.0$/, '')} ${units[i]}`;
}

/** "cargoful.nl/about" from a full address, for source links. */
export function shortUrl(url: string): string {
  try {
    const u = new URL(url);
    const path = u.pathname === '/' ? '' : u.pathname.replace(/\/$/, '');
    const s = `${u.hostname.replace(/^www\./, '')}${path}`;
    return s.length > 48 ? `${s.slice(0, 47)}…` : s;
  } catch {
    return url;
  }
}

/** Short month names as the design writes them: current en-GB locale data spells September "Sept". */
export function shortMonth(text: string): string {
  return text.replace(/\bSept\b/g, 'Sep');
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${formatNumber(n)} ${n === 1 ? one : many}`;
}

const zoneFormats = new Map<string, { day: Intl.DateTimeFormat; time: Intl.DateTimeFormat; date: Intl.DateTimeFormat }>();

function zoneFormat(timeZone: string) {
  let f = zoneFormats.get(timeZone);
  if (!f) {
    f = {
      day: new Intl.DateTimeFormat('en-GB', { timeZone, year: 'numeric', month: 'numeric', day: 'numeric' }),
      time: new Intl.DateTimeFormat('en-GB', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
      date: new Intl.DateTimeFormat('en-GB', { timeZone, weekday: 'short', day: 'numeric', month: 'short' }),
    };
    zoneFormats.set(timeZone, f);
  }
  return f;
}

function validZone(timeZone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-GB', { timeZone });
    return true;
  } catch {
    return false;
  }
}

/**
 * A moment for a sentence, in the reader's time zone: "at 14:32", "tomorrow at 09:00", "on Mon 19 Oct at 09:00"
 * (with the year when it differs). Without a known zone the time is in UTC and says so: "at 14:32 UTC".
 */
export function formatAtIn(d: Date, timeZone: string | null, now = new Date()): string {
  const zone = timeZone && validZone(timeZone) ? timeZone : null;
  const f = zoneFormat(zone ?? 'UTC');
  const ymd = (x: Date) => {
    const p = Object.fromEntries(f.day.formatToParts(x).map((part) => [part.type, Number(part.value)]));
    return { y: p.year!, days: Date.UTC(p.year!, p.month! - 1, p.day!) / 86_400_000 };
  };
  const at = ymd(d);
  const today = ymd(now);
  const time = `at ${f.time.format(d)}${zone ? '' : ' UTC'}`;
  if (at.days === today.days) return time;
  if (at.days === today.days + 1) return `tomorrow ${time}`;
  const day = shortMonth(f.date.format(d)).replace(',', '');
  return `on ${day}${at.y === today.y ? '' : ` ${at.y}`} ${time}`;
}
